// components/SearchableSelect.jsx
import React, { useState, useRef, useEffect } from "react";
import { FaSearch, FaChevronDown, FaTimes } from "react-icons/fa";

const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
  loading = false,
  optionLabelKey = "name",
  optionValueKey = "id",
  getOptionLabel = null,
  getOptionValue = null,
  className = "",
  onSearchChange = null,
  searchPlaceholder = "Search...",
  noResultsText = "No results found",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get label from option
  const getLabel = (option) => {
    if (getOptionLabel) return getOptionLabel(option);
    if (typeof option === "string" || typeof option === "number") return option;
    return (
      option[optionLabelKey] || option.name || option.label || String(option)
    );
  };

  // Get value from option
  const getValue = (option) => {
    if (getOptionValue) return getOptionValue(option);
    if (typeof option === "string" || typeof option === "number") return option;
    return option[optionValueKey] || option.id || option.value || option;
  };

  // Get current selected label
  const getSelectedLabel = () => {
    if (!value) return placeholder;
    const selected = options.find((opt) => getValue(opt) === value);
    return selected ? getLabel(selected) : placeholder;
  };

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) => {
        const label = getLabel(option).toLowerCase();
        return label.includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Calculate dropdown position to prevent overflow
  const calculateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300; // Approximate dropdown height

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
    }
  };

  // Close dropdown when clicking outside - MODIFIED to ignore clicks inside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    // Use mousedown instead of click for better responsiveness
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // REMOVED the scroll handler that was closing the dropdown
  // This was the main issue preventing scrolling

  // Handle window resize to reposition dropdown
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      calculateDropdownPosition();
      setTimeout(() => searchInputRef.current.focus(), 100);
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    const optionValue = getValue(option);
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const clearSearch = () => {
    setSearchTerm("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Prevent scroll propagation when scrolling inside dropdown
  const handleDropdownScroll = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className="searchable-select-wrapper"
      ref={wrapperRef}
      style={{ position: "relative" }}
    >
      <div
        ref={triggerRef}
        className={`searchable-select-trigger ${className} ${disabled ? "disabled" : ""} ${isOpen ? "open" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          padding: "0.25rem 0.5rem",
          fontSize: "0.875rem",
          border: "1px solid #e2e8f0",
          borderRadius: "0.375rem",
          backgroundColor: disabled ? "#f1f5f9" : "white",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "36px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <span
          style={{
            color: value ? "#0f172a" : "#94a3b8",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {loading ? "Loading..." : getSelectedLabel()}
        </span>
        <FaChevronDown
          style={{
            fontSize: "12px",
            color: "#64748b",
            transition: "transform 0.2s",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            marginLeft: "8px",
            flexShrink: 0,
          }}
        />
      </div>

      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="searchable-select-dropdown"
          style={{
            position: "absolute",
            ...(dropdownPosition === "bottom"
              ? {
                  top: "100%",
                  marginTop: "4px",
                }
              : {
                  bottom: "100%",
                  marginBottom: "4px",
                }),
            left: 0,
            width: "100%",
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "0.375rem",
            boxShadow:
              "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            zIndex: 1000,
            maxHeight: "300px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Search Input */}
          <div
            style={{
              padding: "8px",
              borderBottom: "1px solid #e2e8f0",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FaSearch
                style={{
                  position: "absolute",
                  left: "10px",
                  color: "#94a3b8",
                  fontSize: "12px",
                  pointerEvents: "none",
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                style={{
                  width: "100%",
                  padding: "6px 10px 6px 30px",
                  fontSize: "13px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.375rem",
                  outline: "none",
                }}
              />
              {searchTerm && (
                <FaTimes
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "#94a3b8",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                  onClick={clearSearch}
                />
              )}
            </div>
          </div>

          {/* Options List - FIXED SCROLLING */}
          <div
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "250px",
              flex: 1,
            }}
            onWheel={(e) => {
              // Allow wheel scrolling naturally, just prevent propagation
              e.stopPropagation();
            }}
            onScroll={handleDropdownScroll}
          >
            {filteredOptions.length === 0 ? (
              <div
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: "13px",
                }}
              >
                {noResultsText}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = getValue(option);
                const optionLabel = getLabel(option);
                const isSelected = optionValue === value;

                return (
                  <div
                    key={optionValue || index}
                    onClick={() => handleSelect(option)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "13px",
                      backgroundColor: isSelected ? "#eff6ff" : "transparent",
                      color: isSelected ? "#2563eb" : "#0f172a",
                      transition: "background-color 0.2s",
                      borderBottom:
                        index !== filteredOptions.length - 1
                          ? "1px solid #f1f5f9"
                          : "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "#f8fafc";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {optionLabel}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
